"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createTableSimple = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const Table_1 = tslib_1.__importDefault(require("@mui/material/Table"));
const TableBody_1 = tslib_1.__importDefault(require("@mui/material/TableBody"));
const TableHead_1 = tslib_1.__importDefault(require("@mui/material/TableHead"));
/**
 *
 * @description A function for creating TableSimple with custom column and row mappers
 *
 *
 * @example Create a custom column mapper
 * ```tsx
 *  // Create a custom table headers. It is possible to import the standard cells from TableSimpleCells
 *
 *  export type TMyTableHeaders<T extends string> = ITextHeaderCellProps<T>;
 *
 *  export interface IMyTableHeaderProps<T extends string> extends TTableSimpleHeader<T, TMyTableHeaders<T>> {}
 *
 *  const MyTableHeaderCell = <T extends string>(cell: TMyTableHeaders<T>) => {
 *    switch (cell.kind) {
 *      case "text-header":
 *        return <TextHeaderCell key={cell.columnId} {...cell} />;
 *      default:
 *        return <TableCell>N/A</TableCell>;
 *    }
 *  };
 *
 *
 *  export const MyTableHeader = <T extends string>({ columns, ...rest }: IMyTableHeaderProps<T>) => {
 *    return (
 *      <TableRow {...rest}>
 *        {columns?.map((column) => (
 *          <MyTableHeaderCell key={column.columnId} {...column} />
 *        ))}
 *      </TableRow>
 *    );
 *  };
 *
 * ```
 * @example Creating custom cells
 * ```tsx
 *
 *  // Create a custom cell type that will be used in the row mapper
 *
 *  interface IIconCellProps<T extends string> extends ITableSimpleCell<T, "icon">{}
 *
 *  const IconCell = <T extends string>({ ...cellProps }: IIconCellProps<T>) => {
 *    return (
 *      <TableCell {...cellProps}>
 *        <DoDisturbIcon/>
 *      </TableCell>
 *    );
 *  };
 *
 *  export type TMyTableCells<T extends string> = TTableSimpleCells<T> | IIconCellProps<T>;
 *  export type TMyTableCellMap<T extends string> = TTableSimpleCellMap<T, TMyTableCells<T>>;
 *
 * // Create a custom cell for rendering the different cell types. It is possible to import the standard cells from TableSimpleCell.
 *
 *  const MyTableCell = <T extends string>(cell: TMyTableCells<T>) => {
 *    switch (cell.kind) {
 *      case "text":
 *        return <TextCell key={cell.columnId} {...cell} />;
 *      case "tag":
 *        return <TagCell key={cell.columnId} {...cell} />;
 *      case "icon":
 *        return <IconCell key={cell.columnId} {...cell} />;
 *      default:
 *        return <TableCell>N/A</TableCell>;
 *    }
 *  };
 *
 * ```
 *
 * @example Create a custom row mapper
 * ```tsx
 *
 *  // Extend ITableSimpleRow and pass you local cell type to the second argument.
 *
 *  export interface IMyTableRow<T extends string> extends ITableSimpleRow<T, TMyTableCells<T>> {}
 *
 *  const MyTableRow = <T extends string>({ cells, ...rest }: IMyTableRow<T>) => {
 *    return (
 *      <TableRow {...rest}>
 *        {cells?.map((cell, idx) => (
 *          <MyTableCell key={`cell-${idx}-${cell?.columnId}`} {...cell} />
 *        ))}
 *      </TableRow>
 *    );
 *  };
 *
 * ```
 *
 * @example Create a custom table component that uses the custom column and row mappers
 *
 *  export interface IMyTableProps<T extends string,
 *  H extends ITableSimpleCell<T, string> = TMyTableHeaders<T>,
 *  R extends ITableSimpleCell<T, string> = TMyTableCells<T>> extends ITableSimpleProps<T, H, R> {}
 *
 *
 *  export const MyTable = <T extends string>({ HeadProps,
 *    BodyProps,
 *    headers,
 *    rows, ...rest }: IMyTableProps<T>) => {
 *    return createTableSimple({HeadProps, BodyProps, headers, rows, RowMapper: MyTableRow, ColumnMapper: MyTableHeader, ...rest});
 *  };
 *
 *
 *
 */
const createTableSimple = ({ HeadProps, BodyProps, headers, rows, ColumnMapper, RowMapper, ...rest }) => {
    return (react_1.default.createElement(Table_1.default, { ...rest },
        react_1.default.createElement(TableHead_1.default, { ...HeadProps },
            react_1.default.createElement(ColumnMapper, { ...headers })),
        react_1.default.createElement(TableBody_1.default, { ...BodyProps }, rows?.map((row) => react_1.default.createElement(RowMapper, { key: `row-${row?.id}`, ...row })))));
};
exports.createTableSimple = createTableSimple;
